import 'bootstrap-datepicker';
import initOutsourcingManager from './outsourcing-manager';
import { ensureJqueryEl } from '../../utilities/helpers';

/**
 * @class ProductConfigurations
 * @description Manages logic related to the product configuration form. This includes
 * dynamically showing/hiding product options, making AJAX calls for price and preview,
 * and handling validation and UI state based on different page contexts.
 */

export class ProductConfigurations {
    constructor($container, isCalledByGetInstance = false) {
        if (!isCalledByGetInstance) {
            throw new Error("ProductConfigurations: Use getInstance() to create instances.");
        }

        this.$container = $container;
        /**
         * Server-side configuration data passed via a data attribute.
         * @type {object}
         */
        this.config = this.$container.find('#data-for-product-config-js').data('config') || {};

        this.formID = this.config.formID || null;
        this.page = this.config.page || null;
        this.csrfToken = this.config.csrfToken || null;
        this.routes = this.config.routes || {};
        this.productName = this.config.productName || '';
        this.orderId = this.config.orderId || null;

        // Converted global variables to class properties
        this.pendingShowGroupCount = 0;
        this.pendingHideGroupCount = 0;
        this.doesShowOccur = false;
        this.isInitialShowComplete = false;
        this.xhr = null;
        this.outsourcingManager = initOutsourcingManager($container);

        // Get the form and other elements
        if (this.$container.is('form')) {
            this.$form = this.$container;
        } else if (this.$formID) {
            this.$form = $(this.formID);
        } else {
            this.$form = this.$container.find('form').first();
        }
        
        this.$priceSubmitBtn = this.$form.find('#priceSubmit');
        this.$optionSelects = this.$form.find('.option-set select.product-selection');
        this.$zoneInstallBtn = $('#zoneInstall');

        if (this.$form.length) {
            this.init();
        } else {
            console.warn("ProductFlow: Form not found. Script will not run.");
        }
    }
    
    /**
     * @static
     * @method getInstance
     * @description Gets or creates a single instance of ProductConfigurations for a given container.
     * @param {string|jQuery|HTMLElement} container - The container element, jQuery object, or selector string.
     * @returns {ProductConfigurations} The single instance of the class for the provided container.
     */
    static getInstance(container) {
        const $container = ensureJqueryEl(container);

        if (!$container.length) {
            console.warn("ProductConfigurations: Container element not found.");
            return;
        }

        let instance = $container.data('ProductConfigurations');

        if (!instance) {
            instance = new ProductConfigurations($container, true);
            $container.data('ProductConfigurations', instance);
        }

        return instance;
    }

    // -----------------------------------------------
    // Initialization & Setup
    // -----------------------------------------------

    /**
     * @method init
     * @description Entry point for the class, calls all setup methods.
     */
    init() {
        this.attachValidation();
        this.attachEventListeners();
        this.runInitialPageLoadLogic();
    }

    /**
     * @method attachValidation
     * @description Attaches a jQuery validation handler to the form, routing submissions based on the page context.
     * Assumes a jQuery validation plugin is available.
     */
    attachValidation() {
        // Assuming a jQuery validation plugin is available and configured on the form
        this.$form.validate({
            submitHandler: (form) => {
                if (this.page === 'quotes') {
                    this.$priceSubmitBtn.prop('disabled', true).html('Calculating...');
                    let itemNodes = $(form).serializeArray();
                    this.calculatePrice(itemNodes, () => {
                        this.$priceSubmitBtn.prop('disabled', false).html('Get quote');
                    });
                } else if (this.page === 'preview') {
                    this.$priceSubmitBtn.prop('disabled', true).html('Creating Preview...');
                    var resubmitName = 'Recreate';
                    var itemNodes = $(form).serializeArray();
                    this.$priceSubmitBtn.prop('disabled', false).html(resubmitName);
                    $('#lineItemPreviewContainer').show();
                    this.displayPreview();
                }
            }
        });
    }

    /**
     * @method attachEventListeners
     * @description Binds all necessary change and click event listeners to the form elements.
     */
    attachEventListeners() {
        this.$optionSelects
            .on('change', (e) => {
                this.pendingShowGroupCount = 0;
                this.pendingHideGroupCount = 0;
                this.doesShowOccur = false;
                this.isInitialShowComplete = true;
                this.showChildren(e.target).done(() => {
                    // console.log('on change logic $.when promise RESOLVED!');
                    this.checkForChildrenVisibility();
                    this.outsourcingManager.populateItemActions();
                }).fail(() => {
                    // console.log('on change $.when promise REJECTED!');
                    // You can also inspect the rejection reasons
                    // var args = Array.prototype.slice.call(arguments);
                    // console.log(args);
                });
            });

        if (!['zone-edit', 'preview', 'quotes', 'accounts'].includes(this.page)) {
            this.$form.find('[name^=item-nodes]').on('change', () => {
                this.calculatePrice(this.$form.serializeArray(), () => { });
            });
        }

        this.$form.find('input[type=radio][name^="millimeters"]').on('click', (e) => {
            this.toggleMillimeterSizeInputs(e);
            this.calculatePrice(this.$form.serializeArray(), () => { });
        });

        this.$form.find('select.size').on('change', (evt) => {
            this.toggleCustomSizeFields(evt.currentTarget);
        });

        // copied from _create-content.blade inline script
        this.$zoneInstallBtn.on('click', (i, el) => this.toggleInstallInputsToForm($(el).is(':checked')));

        if (!['preview', 'quotes'].includes(this.page)) {
            this.$form.on('submit', (evt) => {
                if (this.$form.valid()) {
                    evt.preventDefault();
                    $('#nextButton').prop('disabled', true).text('Processing...');
                    this.$form.get(0).submit();
                }
            });
        }
    }

    /**
     * @method runInitialPageLoadLogic
     * @description Executes logic on initial page load, such as showing default children and calculating the initial price.
     */
    runInitialPageLoadLogic() {

        // prevent user from seeing fieldsets during initial collapse
        this.$form.find('.option-set').addClass('hide-init');

        const initialPromises = [];
        this.$optionSelects.each((_, select) => {
            initialPromises.push(this.showChildren(select));
        });

        $.when(...initialPromises).done(() => {
            // console.log('initital page logic $.when promise RESOLVED!');
            this.isInitialShowComplete = true;
            this.$form.find('.option-set').removeClass('hide-init');
            this.checkForChildrenVisibility();
            this.outsourcingManager.populateItemActions();
        }).fail(() => {
            // console.log('initital page logic $.when promise REJECTED!');
            // inspect the rejection reasons
            // var args = Array.prototype.slice.call(arguments);
            // console.log(args);
        });

        if (!['preview', 'quotes', 'zone-edit', 'accounts'].includes(this.page)) {
            this.calculatePrice(this.$form.serializeArray(), () => { });
        }

        const formSizeFields = this.$form.find('select.size');

        $(formSizeFields).each((_,selectEl) => {
            this.toggleCustomSizeFields(selectEl);
        });
        
    }

    // -----------------------------------------------
    // API & Data Logic
    // -----------------------------------------------

    /**
     * @method displayPreview
     * @description Fetches and displays a preview of the configured item via an AJAX request.
     */
    displayPreview() {
        let url = this.routes.orderItemPreview;
        if (this.xhr != null) {
            this.xhr.abort();
            this.xhr = null;
        }

        if (!url) return;

        this.xhr = $.ajax({
            url: url,
            data: this.$form.serialize(),
            type: "post",
            success: (data) => {
                $('#lineItemPreview').html(data);
            }
        });
    }

    /**
     * @method calculatePrice
     * @description Calculates the price of the configured item via an AJAX request and updates the UI.
     * @param {Array} itemNodes - The serialized form data.
     * @param {Function} callback - A function to execute after the price has been calculated.
     */
    calculatePrice(itemNodes, callback) {
        if (this.xhr != null) {
            this.xhr.abort();
            this.xhr = null;
        }

        let url;
        if (['item-edit', 'productEditMultiple', 'item-load-change', 'item-change-product', 'productEditMultipleLoad'].includes(this.page)) {
            url = this.routes.orderItemEditPrice;
        } else {
            url = this.routes.orderItemPrice;
        }

        if(!url) return;
        
        this.xhr = $.ajax({
            url: url,
            type: 'POST',
            headers: {
                'X-CSRF-TOKEN': this.csrfToken
            },
            data: itemNodes,
            success: (data) => {
                let response = JSON.parse(data);
                let styleNumberDisplay = $('#styleNumberDisplay');
                if (response.hasOwnProperty('error')) {
                    $('#mainErrorDisplay').html(response.error);
                } else {
                    $('#priceDisplay').html(response.totalPrice);
                    if (styleNumberDisplay.length > 0) {
                        styleNumberDisplay.html('Style Number: ' + response.styleNumber);
                    }
                }
                callback();
            }
        });
    }

    // -----------------------------------------------
    // DOM Manipulation & Rendering
    // -----------------------------------------------
    /**
     * @method toggleMillimeterSizeInputs
     * @description Toggles the labels for size inputs between inches and millimeters.
     * @param {JQueryEventObject} e - If true, sets labels to millimeters; otherwise, to inches.
     */
    toggleMillimeterSizeInputs(e) {
        let useMillimeters = $(e.target).val() === '1';
        const widthLabel = useMillimeters ? 'Width (MM)' : 'Width (IN)';
        const heightLabel = useMillimeters ? 'Height (MM)' : 'Height (IN)';

        $(e.target).parents('.sizeContainer.size-field').find('.customSize, .viewSize').each((_,el) => {
            $(el).find('.width-input').find('.label').html(widthLabel);
            $(el).find('.height-input').find('.label').html(heightLabel);
        });
    }

    /**
     * @method showFields
     * @description Animates a set of fields to be visible and enabled.
     * @param {jQuery} fields - The jQuery object containing the fields to show.
     * @param {boolean} isShowChildrenToggle - If true, handles post-animation logic for the showChildren flow.
     */
    showFields(fields, isShowChildrenToggle) {
        const promises = [];

        if (!$(fields).length) {
            return $.Deferred().resolve().promise();
        }

        $.each(fields, (_, el) => {
            const deferred = $.Deferred();

            // Check if the element is already visible. If so, resolve immediately.
            if ($(el).is(':visible')) {
                deferred.resolve();
            }
            else if (isShowChildrenToggle) {
                $(el).slideDown(400, () => {
                    this.pendingShowGroupCount--;
                    if (this.pendingShowGroupCount === 0 && this.isInitialShowComplete) {
                        // this.outsourcingManager.populateItemActions();
                    }
                    deferred.resolve();
                }).prop('disabled', false);
            } else {
                $(el).slideDown().prop('disabled', false).addClass('is-open');
                deferred.resolve();
            }
        });

        return $.when(...promises).promise();
    }

    /**
     * @method hideFields
     * @description Animates a set of fields to be hidden and disabled.
     * @param {jQuery} fields - The jQuery object containing the fields to hide.
     * @param {boolean} shouldPopulate - If true, handles post-animation logic for the showChildren flow.
     */
    hideFields(fields, shouldPopulate) {
        
        const promises = [];

        if(!$(fields).length) {
            return $.Deferred().resolve().promise();
        }

        $.each(fields, (_,el) => {
            const deferred = $.Deferred();

            // Check if the element is already hidden. If so, resolve immediately.
            if ($(el).is(':hidden') &&
                !(!$(el).width() && !$(el).height() && $(el).css('display') === 'block'))// the size fields have no width and height but are display block
                {
                deferred.resolve();
            }
            else if (shouldPopulate) {
                $(el).slideUp(400, () => {
                    this.pendingHideGroupCount--;
                    if (!this.doesShowOccur && this.pendingHideGroupCount === 0 && this.isInitialShowComplete) {
                        // this.outsourcingManager.populateItemActions();
                    }
                    deferred.resolve();
                }).prop('disabled', true);
            } else {
                $(el).slideUp().prop('disabled', true).removeClass('is-open');
                deferred.resolve();
            }

            promises.push(deferred.promise());
        });

        return $.when(...promises).promise();
    }

    /**
     * @method showChildren
     * @description This script manages a nested, tree-like form structure using data attributes and classes. Each selection reveals a new level of options, creating a parent-child relationship.

        - data-node: A unique ID on an <option> element that represents a specific choice.
        - data-parent: An attribute on a <fieldset> or <li> that links it to its parent's data-node.
        - .option-set: A <fieldset> container for a group of related <select> dropdowns. The script shows and hides these groups based on a parent selection.
        - .is-open: A class added or removed by the script to control the visibility of a container.

     The logic follows a simple flow: an option's data-node becomes the data-parent for the next level of fields, creating a dynamic, one-to-one relationship between a selection and its dependent options.
     * @param { HTMLElement } select - The HTML select element that triggered the change.
     */
    showChildren(select) {
        if (!$(select).hasClass('product-selection')) { 
            return $.Deferred().resolve().promise();
        }

        const $select = $(select);
        const $selected = $select.find(':selected');
        const node = $selected.data('node');
        const isSelected = node !== undefined && node > 0;

        const parentNode = $select.closest('.option-set').data('parent');
        const $allChildren = this.$form.find(`fieldset[data-parent="${parentNode}"]:not('.option-group-parent')`);
        
        //  array of deferred objects to track animations.
        let actions = [];

        // hide all fieldsets within the current option-set that don't match the selection
        const $parentFieldset = $select.closest('fieldset');
        const $allSiblings = $parentFieldset.siblings('fieldset');
        $parentFieldset.find('fieldset').each((_, fieldset) => {
            const $fieldset = $(fieldset);
            const fieldsetParentNode = $fieldset.data('parent');
            if (fieldsetParentNode && fieldsetParentNode !== node) {
                actions.push(this.hideFields($(fieldset), true));
            }
        });

        if (isSelected) {
            const parentSelector = `[data-parent=${node}]`;
            const $childFieldsets = this.$form.find(`fieldset${parentSelector}`);

            actions.push(this.showFields($childFieldsets, true));

            // Recursively call and collect promises from nested calls
            $childFieldsets.find(`select${parentSelector}`).each((_, childSelect) => {
                const childPromise = this.showChildren(childSelect);
                actions.push(childPromise);
            });


            // Handle logic for when there are no more children to show
            if ($childFieldsets.length === 0) {
                // this.outsourcingManager.populateItemActions();
            }

        } else { // If nothing is selected, ensure no children are shown
            // this.outsourcingManager.populateItemActions();
        }

        // Toggle the product configuration and add its promise to the array
        actions.push(this.toggleProductConfiguration(select));

        if (actions.length === 0) {
            return $.Deferred().resolve().promise();
        }

        // Return a single promise that resolves when all actions are complete
        return $.when(...actions).promise();
    }

    /**
     * @method checkForChildrenVisibility
     * @description Iterates through option sets and order lists to toggle a 'hidden-children' class based on the visibility of their direct descendants.
     * If no visible children are found, the 'hidden-children' class is added to the parent element; otherwise, it is removed.
     * @returns {void}
     */
    checkForChildrenVisibility = () => {
        this.$form.find('.option-set, .order-options-list').each((_, el) => {
            let hasVisibleChildren = false;
            $(el).children().each((_, child) => {
                const $child = $(child);
                // Check for visibility and height to be > 1 to avoid weird JS issues.
                if ($child.is(':visible') && $child.outerHeight() > 1 && !$child.hasClass('hidden-children')) {
                    hasVisibleChildren = true;
                    return false;
                }
            });
            $(el).toggleClass('hidden-children', !hasVisibleChildren);
        });
    }

    /**
     * @method toggleProductConfiguration
     * @description Hides and shows product configuration sections based on the selected option.
     * @param {HTMLElement} select - The HTML select element that was changed.
     */
    toggleProductConfiguration(select) {
        const unselected = [];
        let selectedNodeID;

        for (let i = 0; i < select.options.length; i++) {
            const option = select.options[i];
            const nodeID = $(option).data('node');
            if (option.selected) {
                selectedNodeID = nodeID;
            } else {
                unselected.push(nodeID);
            }
        }

        const $configToShow = this.$form.find(`#productConfig${selectedNodeID}`);
        this.showFields($configToShow, false);
        this.showFields($configToShow.find(':input'), false);

        for (let i = 0; i < unselected.length; i++) {
            const $configToHide = this.$form.find(`#productConfig${unselected[i]}`);
            
            this.hideFields($configToHide, false);
            this.hideFields($configToHide.find(':input'), false);
        }
    }

    /**
     * Toggles a set of hidden installation-related input fields.
     *
     * When `shouldAdd` is true, this method dynamically appends a new set of hidden inputs
     * for an installation item to the form. This is typically used to include
     * installation details in a form submission. When `shouldAdd` is false,
     * it removes the previously added inputs.
     *
     * @param {boolean} shouldAdd - If true, the hidden installation inputs are appended to the form.
     * If false, the inputs are removed.
     */
    toggleInstallInputsToForm = (shouldAdd) => {
        if (shouldAdd) {
            $(this.$form).append(
                $('<div>').attr('id', 'hiddenInstallationInputs').append(
                    $('<input>').attr({
                        type: 'hidden',
                        name: 'items-installation[name]',
                        value: 'Installation for ' + this.productName
                    })
                ).append(
                    $('<input>').attr({
                        type: 'hidden',
                        name: 'items-installation[orderID]',
                        value: this.orderId
                    })
                ).append(
                    $('<input>').attr({
                        type: 'hidden',
                        name: 'items-installation[status]',
                        value: 'Pending'
                    })
                )
            );
        } else {
            $('#hiddenInstallationInputs').remove();
        }
    }

    toggleCustomSizeFields = (selectEl) => {
        if (!selectEl) {
            return;
        }

        const $selectEl = $(selectEl);
        const isCustomSize = $selectEl.val() === '';
        const $customSizeInputs = $(selectEl)
            .closest('.size-field')
            .find('.customSize');

        if(isCustomSize) {
            $customSizeInputs.slideDown();
        } else {
            $customSizeInputs.slideUp();
        }

        $customSizeInputs
            .prop('disabled', !isCustomSize)
            .toggleClass('is-open', isCustomSize);
    }
}

const initProductConfigurations = (container) => {
    ProductConfigurations.getInstance(container);
}
export default initProductConfigurations;



